import { Component, HostBinding, Input, ViewEncapsulation } from '@angular/core';
import { TagTextData } from '../tag-text/tag-text.component';
import { AdType } from "../ads-banners/ads-banners.component";
import { UtilsService } from 'projects/common/services/utils.service';
import { MediaPictureService } from 'projects/common/services/media-picture.service';
import { UpaService, UpaTarget } from 'projects/common/services/upa.service';
import { GtmService } from 'projects/common/services/gtm.service';
import { ScrollService } from 'projects/common/services/scroll.service';
import { MetadataService } from 'projects/common/services/metadata.service';

export type Banner = {
  ad: boolean;
  id?: string;
  title?: TagTextData;
  text?: TagTextData;
  image: string;
  link: string;
  scroll_target: string;
  alt: string;
  original_position?: number;
}

@Component({
  selector: 'banner',
  template: `
    <!-- Banner normal (sin wrapper de ads) -->
    <div *ngIf="!isAd || !adBannerType" class="slide" 
      [ngClass]="{'rounded': rounded}"
      [ngStyle]="this.minHeight ? {'min-height': this.minHeight + 'px'} : {}">
      <ng-container *ngTemplateOutlet="bannerContent"></ng-container>
    </div>

    <!-- Wrapper condicional para ads-banners -->
    <div (click)="handleLinkClick($event)"
         *ngIf="isAd && adBannerType" 
         class="clickable slide {{rounded ? 'rounded' : ''}}"
         [ngStyle]="this.minHeight ? {'min-height': this.minHeight + 'px'} : {}">
      <ads-banners [bannerType]="adBannerType" [moduleId]="moduleId"></ads-banners>
      <div class="fallback-banner">
        <ng-container *ngTemplateOutlet="bannerContent"></ng-container>
      </div>
    </div>

    <!-- Template común para el contenido del banner -->
    <ng-template #bannerContent>
      <a class="link" *ngIf="banner.link else content" 
         [href]="banner.link" 
         (click)="!isAd && handleLinkClick($event)" 
         [attr.aria-label]="utils.linkToName(banner.link)">
        <ng-container *ngTemplateOutlet="content"></ng-container>
      </a>

      <ng-template #content>
        <div (click)="handleScrollClick($event)" 
             class="multi-banner-wrapper"
             [ngClass]="{'cursor-pointer': banner.scroll_target && banner.scroll_target !== ''}">
          <img class="picture" 
               loading="lazy" 
               [src]="mediaService.getMediaUrl(banner.image, (bannersSize[index] || bannersSize[0]))" 
               [alt]="makeAlt()"/>
          <div class="banner-content -eva-3-p-xlg">
            <tag-text *ngIf="banner.title" [data]="banner.title"></tag-text>
            <tag-text *ngIf="banner.text" [data]="banner.text"></tag-text>
          </div>
        </div>
      </ng-template>
    </ng-template>
  `,
  encapsulation: ViewEncapsulation.None
})
export class BannerComponent {
  @Input() banner: Banner;
  @Input() index: number;
  @Input() bannersNumber: number;
  @Input() bannersSize: string[];
  @Input() moduleId: string;
  @Input() uniqueId: string | undefined;
  @Input() moduleIndex: number;
  @Input() adBannerType: AdType | undefined;
  @Input() minHeight: number | undefined;
  @Input() rounded: boolean = true;

  @HostBinding('attr.banner-id')
  get hostBannerId(): string | null {
    return this.banner?.id || null;
  }

  constructor(
    public utils: UtilsService,
    public mediaService: MediaPictureService,
    private upaService: UpaService,
    private gtmService: GtmService,
    private metadataService: MetadataService
  ) {}

  get isAd(): boolean {
    return this.banner.ad && !!this.adBannerType;
  }

  makeAlt(): string {
    return this.banner.alt || this.banner.title?.text || this.banner.text?.text || '';
  }

  trackGtm(link: string): void {
    const trackingData = {
      label: this.banner.id || this.banner.title?.text || this.banner.text?.text || 'image',
      moduleId: this.moduleId,
      moduleIndex: this.moduleIndex,
      positionIndex: this.index,
      target: UpaTarget.BANNER,
      href: link,
      uniqueId: this.uniqueId
    };
    this.upaService.trackClickTopic(trackingData, undefined, true);
    this.gtmService.trackEventIfApplicable("user-action", "multi-banner-" + this.index, "Click", link);
  }

  handleLinkClick(event: MouseEvent): void {
    this.trackGtm(this.banner.link);
    
    const hasIframeChild: boolean = !!(event.currentTarget as HTMLElement).querySelector('iframe');
    if (hasIframeChild) {
      return;
    }
    event.preventDefault();
    const pageLoad = !this.metadataService.isXDespShowInfoEnabled() || confirm("¿Ir al enlace?\n" + this.banner.link);
    if (pageLoad) window.open(this.banner.link, '_self');
  }

  handleScrollClick(event: MouseEvent): void {
    if (this.banner.scroll_target) {
      this.trackGtm(this.banner.scroll_target);
      ScrollService.scrollTo(this.banner.scroll_target);
    }
  }
}
